import React, { useEffect, useRef, useState } from 'react';
import { accountingApi } from '../../services/accountingApi';
import { Receipt } from '../../types/accounting';

interface EntryAttachmentsProps {
  businessId: number;
  entryId: number;
}

export const MAX_ATTACHMENTS_PER_ENTRY = 3;

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Invoices, receipts and other support for one journal entry: list, attach (photo or PDF, 10MB, 3 per entry), remove */
const EntryAttachments: React.FC<EntryAttachmentsProps> = ({ businessId, entryId }) => {
  const [attachments, setAttachments] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    accountingApi.getReceipts(businessId, entryId)
      .then(response => {
        if (!cancelled && response.status === 'success') setAttachments(response.data?.receipts || []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.message || 'Failed to load attachments');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [businessId, entryId]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const response = await accountingApi.uploadReceipt(businessId, entryId, file);
      if (response.status === 'success' && response.data?.receipt) {
        setAttachments(prev => [...prev, response.data!.receipt]);
      } else {
        setError(response.message || 'Upload failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = ''; // so the same file can be picked again
    }
  };

  const handleRemove = async (receiptId: number) => {
    try {
      await accountingApi.deleteReceipt(receiptId);
      setAttachments(prev => prev.filter(r => r.id !== receiptId));
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to remove attachment');
    }
  };

  const remaining = MAX_ATTACHMENTS_PER_ENTRY - attachments.length;

  return (
    <div className="entry-attachments">
      <div className="entry-attachments-title">
        <strong>Attachments</strong>
        {loading && <span>Loading...</span>}
      </div>
      {error && <div className="entry-attachments-error">{error}</div>}
      {!loading && attachments.length === 0 && <div className="entry-attachments-empty">Nothing attached yet</div>}
      {attachments.map(attachment => (
        <div key={attachment.id} className="entry-attachment">
          <span className="entry-attachment-kind">{attachment.mime_type === 'application/pdf' ? 'PDF' : 'IMG'}</span>
          <a href={accountingApi.getReceiptUrl(attachment.id)} target="_blank" rel="noopener noreferrer">
            {attachment.original_filename}
          </a>
          <span className="entry-attachment-size">{formatFileSize(attachment.file_size)}</span>
          <button type="button" onClick={() => handleRemove(attachment.id)} title="Remove attachment">x</button>
        </div>
      ))}
      {remaining > 0 && (
        <div className="entry-attachments-add">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.heic,.heif,application/pdf"
            onChange={handleUpload}
            style={{ display: 'none' }}
          />
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? 'Uploading...' : '+ Attach invoice, photo or PDF'}
          </button>
          <span className="entry-attachments-hint">Max 10MB each, {remaining} of {MAX_ATTACHMENTS_PER_ENTRY} left</span>
        </div>
      )}
    </div>
  );
};

export default EntryAttachments;
